import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { Spinner, Callout } from "@/design/components";
import { useAuth } from "@/app/auth";
import styles from "./AuthForm.module.css";

/**
 * Landing for the social-login redirect (/auth/social). The API put the result in the
 * URL FRAGMENT (never the query/logs): `#token=<jwt>` on success. We read it, scrub it
 * from history, establish the session (fetches the authoritative identity), and go to the
 * app. A failed/absent token sends the user to /login (the API uses /login#error on its
 * own failures).
 */
export function SocialCallbackScreen() {
  const navigate = useNavigate();
  const { establishSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard (the token is single-use server-side anyway)
    ran.current = true;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = params.get("token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    // Remove the token from the address bar/history before doing anything else.
    window.history.replaceState(null, "", window.location.pathname);
    establishSession(token)
      .then(() => navigate("/app", { replace: true }))
      .catch(() => setError("We couldn't complete sign-in. Please try again."));
  }, [establishSession, navigate]);

  return (
    <AuthLayout>
      {error ? (
        <Callout tone="danger" title="Sign-in failed">
          {error} <Link to="/login">Back to sign in</Link>
        </Callout>
      ) : (
        <div className={styles.callbackPending}>
          <Spinner size={22} color="var(--accent)" /> <span>Signing you in…</span>
        </div>
      )}
    </AuthLayout>
  );
}
